import React from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiLayers, FiGrid, FiMessageCircle } from 'react-icons/fi';
import PublishedPageLayout from './PublishedPageLayout';
import {
    Page,
    MainColumn,
    ContentPane,
    HeaderBar,
    TitleGroup,
    ViewToggle,
    ViewButton,
    StateMessage,
    GridScroll
} from './publishedMessageStyles';
import ReadOnlyMessageChat from '../../components/ReadOnlyContent/ReadOnlyMessageChat';
import ReadOnlyMessageCard from '../../components/ReadOnlyContent/ReadOnlyMessageCard';
import ReadOnlyMessageGrid from '../../components/ReadOnlyContent/ReadOnlyMessageGrid';
import ReadOnlyMessageSidePreview from '../../components/ReadOnlyContent/ReadOnlyMessageSidePreview';
import { decodeDisplayText } from '../../util/displayText';
import { GET_PUBLISHED_SHARE_COLLECTION } from '../../gql-operations/queries/sharing/share-collections';
import { WHITE } from '../../appearance/Colors';

// Build display-only messages from the public element allow-list. No source IDs,
// folder permissions, hidden metadata, or private message lookups are used.
export function collectionDisplayMessages(collection) {
    const comparable = text =>
        String(decodeDisplayText(text) || '')
            .normalize('NFC')
            .replace(/\s+/gu, ' ')
            .trim();
    const mediaObject = element => {
        const media = element.media || {};
        const type = element.kind.toLowerCase();
        return {
            url: media.url || media.cdnUrl,
            cdn_url: media.cdnUrl,
            type,
            mime: media.mime,
            format: media.format,
            name: `shared.${
                media.format || (type === 'video' ? 'mp4' : type === 'audio' ? 'mp3' : type === 'image' ? 'jpg' : 'bin')
            }`,
            size: media.size,
            width: media.width,
            height: media.height,
            duration: media.duration,
            thumbnail: { md: media.thumbnailUrl }
        };
    };
    return (collection?.items || []).flatMap((item, index) => {
        if (item.available === false || !item.elements.length) return [];
        const title = item.elements.find(element => element.kind === 'TITLE')?.text || '';
        const linkPreview = item.elements.find(element => element.kind === 'TITLE')?.linkPreview;
        const note = item.elements.find(element => element.kind === 'NOTE')?.text || '';
        const transcript = item.elements.find(element => element.kind === 'TRANSCRIPT')?.text || '';
        const article = item.elements
            .filter(element => element.kind === 'ARTICLE')
            .map(element => element.text)
            .filter(Boolean)
            .join('\n\n');
        const attachments = item.elements.filter(element => element.media);
        const image = attachments.find(element => element.kind === 'IMAGE');
        const file = attachments.find(element => element.kind !== 'IMAGE');
        const extras = attachments.filter(element => element !== image && element !== file);
        const body = [!title || comparable(note) !== comparable(title) ? note : '', transcript, article]
            .filter(Boolean)
            .join('\n\n');
        return [
            {
                id: `collection:${collection.id}:${index}`,
                title,
                body,
                ...(image && !linkPreview ? { image: mediaObject(image) } : {}),
                ...(file ? { file: mediaObject(file) } : {}),
                ...(transcript ? { transcript: { text: transcript } } : {}),
                ...(extras.length || linkPreview
                    ? {
                          urlMeta: [
                              {
                                  ...(linkPreview
                                      ? {
                                            url: linkPreview.url,
                                            title: linkPreview.title,
                                            description: linkPreview.description,
                                            publisher: linkPreview.publisher,
                                            ...(image
                                                ? {
                                                      image: image.media.url || image.media.cdnUrl,
                                                      image_cdn_url: image.media.cdnUrl
                                                  }
                                                : {})
                                        }
                                      : {}),
                                  gallery: extras.map(element => ({
                                      type: element.kind.toLowerCase(),
                                      image:
                                          element.kind === 'IMAGE'
                                              ? element.media.cdnUrl || element.media.url
                                              : element.media.thumbnailUrl,
                                      media: mediaObject(element)
                                  }))
                              }
                          ]
                      }
                    : {})
            }
        ];
    });
}

export default function PublishedShareCollectionPage({ token }) {
    const { t: lnT } = useTranslation();
    const [count, setCount] = React.useState(20);
    const [view, setView] = React.useState('chat');
    const [selection, setSelection] = React.useState(null);
    const { data, previousData, loading, error } = useQuery(GET_PUBLISHED_SHARE_COLLECTION, {
        variables: { token, count },
        fetchPolicy: 'cache-first',
        pollInterval: 30000,
        notifyOnNetworkStatusChange: true
    });
    const collection = (data || previousData)?.getPublishedShareCollection;
    React.useEffect(() => {
        setSelection(null);
    }, [collection?.items]);
    const messages = React.useMemo(() => collectionDisplayMessages(collection), [collection]);
    const selected =
        !error && selection?.items === collection?.items
            ? messages.find(message => message.id === selection?.id)
            : null;
    const hasMore = Boolean(collection?.cursor) && count < 100;
    const loadMore = () => {
        if (!loading && hasMore) setCount(value => Math.min(100, value + 20));
    };
    const open = message => setSelection({ id: message.id, items: collection.items });
    React.useEffect(() => {
        document.title = `${collection?.title || lnT('messages:sharedCollection')} — Luckynote`;
    }, [collection?.title, lnT]);
    const header =
        collection && !error ? (
            <HeaderBar>
                <TitleGroup>
                    <FiLayers size={18} />
                    <h1>{collection.title || lnT('messages:sharedCollection')}</h1>
                </TitleGroup>
                <ViewToggle aria-label={lnT('messages:sharedCollection')}>
                    <ViewButton
                        type="button"
                        aria-label={lnT('folders:chatView')}
                        aria-pressed={view === 'chat'}
                        $active={view === 'chat'}
                        onClick={() => setView('chat')}
                    >
                        <FiMessageCircle size={15} />
                    </ViewButton>
                    <ViewButton
                        type="button"
                        aria-label={lnT('folders:gridView')}
                        aria-pressed={view === 'grid'}
                        $active={view === 'grid'}
                        onClick={() => setView('grid')}
                    >
                        <FiGrid size={15} />
                    </ViewButton>
                </ViewToggle>
            </HeaderBar>
        ) : null;
    return (
        <PublishedPageLayout fillViewport headerContent={header}>
            <Page>
                {error ? (
                    <StateMessage role="alert">{lnT('messages:sharedMessageUnavailable')}</StateMessage>
                ) : !collection ? (
                    <StateMessage>
                        {loading ? lnT('common:loading') : lnT('messages:sharedMessageUnavailable')}
                    </StateMessage>
                ) : (
                    <>
                        <MainColumn>
                            <ContentPane $isGrid={view === 'grid'}>
                                {!messages.length ? (
                                    <StateMessage>{lnT('messages:collectionContentUnavailable')}</StateMessage>
                                ) : view === 'chat' ? (
                                    <ReadOnlyMessageChat
                                        messages={messages}
                                        preserveOrder
                                        onSelectMessage={open}
                                        onLoadMore={loadMore}
                                        hasMore={hasMore}
                                        loadingMore={loading}
                                    />
                                ) : (
                                    <GridScroll
                                        onScroll={event => {
                                            const el = event.currentTarget;
                                            if (el.scrollHeight - el.scrollTop - el.clientHeight < 320) loadMore();
                                        }}
                                    >
                                        <ReadOnlyMessageGrid>
                                            {messages.map(message => (
                                                <ReadOnlyMessageCard
                                                    key={message.id}
                                                    message={message}
                                                    onClick={() => open(message)}
                                                />
                                            ))}
                                        </ReadOnlyMessageGrid>
                                    </GridScroll>
                                )}
                            </ContentPane>
                            {hasMore ? (
                                <MoreButton disabled={loading} onClick={loadMore}>
                                    {lnT('messages:loadMore')}
                                </MoreButton>
                            ) : null}
                        </MainColumn>
                        {selected ? (
                            <ReadOnlyMessageSidePreview message={selected} onClose={() => setSelection(null)} />
                        ) : null}
                    </>
                )}
            </Page>
        </PublishedPageLayout>
    );
}
const MoreButton = styled.button`
    align-self: center;
    padding: 10px 18px;
    margin: 8px;
    color: ${WHITE};
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    cursor: pointer;
`;
